import React, { useCallback, useEffect, useRef, useState } from "react";
import { ImagePoint } from "../../../data/image-point";
import { ZONE_LAYOUT_ACTIVE_ASPECT_RATIO } from "../inventory-vars.icss.scss";
import whitelabel from "../../../whitelabel";

export interface ZoneLayoutPreviewShapesProps {
	className?: string;

	// Array of polygons to render, each polygon is an array of points
	// Points are in normalized coordinates (0.0 to 1.0) within active area
	polygons: ImagePoint[][];
}

const COLOR_FILL = whitelabel.palette.primary900;

/**
 * A layer that renders static polygons in solid blue with no user interaction.
 * - Renders multiple polygons from an array
 * - Solid blue fill
 * - No editing capabilities
 * - No transformation support
 * - Points are in normalized coordinates (0.0 to 1.0) within the active area
 */
export default function ZoneLayoutPreviewShapes(
	props: ZoneLayoutPreviewShapesProps,
) {
	//A ref to the canvas element
	const canvasRef = useRef<HTMLCanvasElement | null>(null);

	//The size of the canvas element
	const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });

	/// Gets the active area bounds relative to the canvas in px
	const getActiveAreaBounds = useCallback(() => {
		//Resolve the size of the internal canvas
		const canvas = canvasRef.current!;
		const canvasWidth = canvas.clientWidth;
		const canvasHeight = canvas.clientHeight;

		//Resolve the active area within the canvas
		const activeAreaScale = Math.min(
			canvasWidth / ZONE_LAYOUT_ACTIVE_ASPECT_RATIO,
			canvasHeight * ZONE_LAYOUT_ACTIVE_ASPECT_RATIO,
		);
		const activeAreaWidth =
			activeAreaScale * ZONE_LAYOUT_ACTIVE_ASPECT_RATIO;
		const activeAreaHeight =
			activeAreaScale / ZONE_LAYOUT_ACTIVE_ASPECT_RATIO;
		const activeAreaX = (canvasWidth - activeAreaWidth) / 2;
		const activeAreaY = (canvasHeight - activeAreaHeight) / 2;

		return {
			x: activeAreaX,
			y: activeAreaY,
			width: activeAreaWidth,
			height: activeAreaHeight,
		};
	}, []);

	// Convert normalized coordinates to canvas coordinates for rendering
	const normalizedToCanvas = useCallback(
		(point: ImagePoint): ImagePoint => {
			//Get the active area
			const area = getActiveAreaBounds();

			//Convert from normalized to active area coordinates
			const x = area.x + point.x * area.width;
			const y = area.y + point.y * area.height;

			return { x, y };
		},
		[getActiveAreaBounds],
	);

	// Draw the polygons on the canvas
	useEffect(() => {
		const canvas = canvasRef.current!;
		const context = canvas.getContext("2d")!;

		// Get canvas dimensions
		const canvasWidth = canvas.clientWidth * devicePixelRatio;
		const canvasHeight = canvas.clientHeight * devicePixelRatio;

		// Update canvas internal dimensions
		canvas.width = canvasWidth;
		canvas.height = canvasHeight;

		// Clear the canvas
		context.reset();
		context.clearRect(0, 0, canvasWidth, canvasHeight);

		//Apply high-DPI scaling
		context.scale(devicePixelRatio, devicePixelRatio);

		// Render each polygon
		for (const polygon of props.polygons) {
			// Skip polygons with less than 3 points
			if (polygon.length < 3) {
				continue;
			}

			// Convert normalized points to canvas coordinates
			const canvasPoints = polygon.map((point) =>
				normalizedToCanvas(point),
			);

			// Draw the polygon fill
			context.beginPath();
			context.moveTo(canvasPoints[0].x, canvasPoints[0].y);

			for (let i = 1; i < canvasPoints.length; i++) {
				context.lineTo(canvasPoints[i].x, canvasPoints[i].y);
			}

			// Close the path and fill
			context.closePath();
			context.fillStyle = COLOR_FILL;
			context.fill();
		}
	}, [props.polygons, normalizedToCanvas, canvasSize]);

	// Update canvas size when container resizes
	useEffect(() => {
		const canvas = canvasRef.current!;

		const updateCanvasSize = () => {
			const rect = canvas.getBoundingClientRect();
			setCanvasSize({ width: rect.width, height: rect.height });
		};

		updateCanvasSize();

		// Use ResizeObserver to handle container size changes
		const resizeObserver = new ResizeObserver(updateCanvasSize);
		resizeObserver.observe(canvas);

		return () => {
			resizeObserver.disconnect();
		};
	}, [setCanvasSize]);

	return <canvas className={props.className} ref={canvasRef} />;
}
